/**
 * Web components (03-design-system § 4). The only folder that may import
 * `react-dom` types; mobile has its own set in `apps/mobile/src/ui`.
 *
 * Every colour comes from the Tailwind preset, which is generated from
 * `tokens.ts` — no component writes a hex value (03 § 1 note 1).
 */

import { useEffect, useRef } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { deskAccessibilityLabel, deskStyle, hitRect, viewBoxFor, type PlanObject } from '../ui-core/index.js';
import type { DeskState } from '../tokens/index.js';
import { formatMoney, formatPeriod, type Period } from '../format/index.js';

/* --------------------------------- Button --------------------------------- */

export type ButtonVariant = 'primary' | 'secondary' | 'ink' | 'lime' | 'danger' | 'ghost';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-teal text-on-teal hover:bg-teal-dark',
  secondary: 'bg-white text-ink border border-line-strong hover:bg-fog',
  // "Mit Apple fortfahren" is black by Apple's HIG, on iOS and Android alike
  // (06 § 5.1). A variant, not a `className` override: two `bg-*` utilities of
  // equal specificity are decided by their order in the stylesheet, not by the
  // order they are written in.
  ink: 'bg-ink text-white hover:bg-ink/90',
  // 03 § 1 note 2 — at most one lime element per screen.
  lime: 'bg-lime text-on-lime hover:brightness-95',
  danger: 'bg-danger-bg text-danger-text hover:brightness-95',
  ghost: 'bg-transparent text-teal-dark hover:bg-fog',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** 03 § 4.1 — `lg` is 52 high, `sm` is 36; both keep the same hit padding. */
  size?: 'lg' | 'md' | 'sm';
  loading?: boolean;
  full?: boolean;
}

const BUTTON_SIZES = {
  lg: 'rounded-button h-button px-6 text-base',
  /** The backoffice's `.btn`: 40 px, 10 px radius, 14 px — the editor bars. */
  md: 'h-10 rounded-[10px] px-4 text-sm',
  sm: 'h-9 rounded-[10px] px-4 text-sm',
} as const;

export function Button({
  variant = 'primary',
  size = 'lg',
  loading,
  full,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      aria-busy={loading || undefined}
      className={[
        // `gap` so a provider glyph sits 10 px from its label (09 § 4).
        'font-body inline-flex items-center justify-center gap-2.5 whitespace-nowrap font-semibold',
        BUTTON_SIZES[size],
        'disabled:bg-disabled disabled:text-placeholder transition-colors disabled:cursor-not-allowed',
        // 03 § 9 — a visible focus ring everywhere, keyboard or not.
        'focus-visible:ring-teal-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        BUTTON_VARIANTS[variant],
        full ? 'w-full' : '',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

/* ---------------------------------- Card ---------------------------------- */

export function Card({
  children,
  className,
  'data-testid': testId,
}: {
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={`rounded-webCard border-line bg-white p-6 shadow-sm ${className ?? ''}`}
    >
      {children}
    </div>
  );
}

export function Row({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="border-hairline flex items-baseline justify-between gap-4 border-b py-3 last:border-b-0">
      <span className="font-body text-muted text-sm">{label}</span>
      <span className="font-body text-ink text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
}

/* ---------------------------------- Chip ---------------------------------- */

export type ChipTone = 'neutral' | 'teal' | 'success' | 'warn' | 'danger' | 'lime';

const CHIP_TONES: Record<ChipTone, string> = {
  neutral: 'bg-fog text-muted',
  teal: 'bg-teal-tint text-teal-dark',
  success: 'bg-success-bg text-success-text',
  warn: 'bg-warn-bg text-warn-text',
  danger: 'bg-danger-bg text-danger-text',
  lime: 'bg-lime text-ink',
};

export interface ChipProps {
  label: ReactNode;
  tone?: ChipTone;
  size?: 'sm' | 'md';
  /**
   * Selectable chips (filters, cancel reasons) look different from status
   * chips: white with a border until chosen, then tinted — 03 § 4.3.
   */
  selected?: boolean;
  onPress?: () => void;
  testID?: string;
}

export function Chip({ label, tone = 'neutral', size = 'sm', selected, onPress, testID }: ChipProps) {
  const height = size === 'sm' ? 'h-6' : 'h-8';
  const skin =
    selected === undefined
      ? CHIP_TONES[tone]
      : selected
        ? 'bg-ink text-white'
        : 'border-line-strong text-text border bg-white';
  const className = `rounded-pill font-body inline-flex ${height} items-center gap-1.5 whitespace-nowrap px-2.5 text-xs font-semibold ${skin}`;

  if (!onPress) {
    return (
      <span className={className} data-testid={testID}>
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onPress}
      className={className}
      data-testid={testID}
      aria-pressed={selected}
    >
      {label}
    </button>
  );
}

/* ---------------------------------- Field --------------------------------- */

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** An i18n-resolved message; the field is only invalid when this is set. */
  error?: string | null;
  hint?: string;
}

export function Field({ label, error, hint, id, className, ...rest }: FieldProps) {
  const inputId = id ?? `field-${rest.name ?? label}`;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      <label
        htmlFor={inputId}
        className="font-body text-muted text-xs font-semibold uppercase tracking-wider"
      >
        {label}
      </label>
      <input
        {...rest}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={[
          'rounded-input h-field font-body text-ink border px-4',
          'focus-visible:ring-teal-dark focus-visible:outline-none focus-visible:ring-2',
          error ? 'border-danger-text' : 'border-line-strong',
        ].join(' ')}
      />
      {error ? (
        <p id={`${inputId}-error`} role="alert" className="font-body text-danger-text text-sm">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="font-body text-muted text-sm">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/* --------------------------------- Banner --------------------------------- */

export type BannerTone = 'info' | 'success' | 'warning' | 'danger';

const BANNER_TONES: Record<BannerTone, string> = {
  info: 'bg-teal-tint text-teal-dark',
  success: 'bg-success-bg text-success-text',
  warning: 'bg-warn-bg text-warn-text',
  danger: 'bg-danger-bg text-danger-text',
};

export function Banner({ tone = 'info', children }: { tone?: BannerTone; children: ReactNode }) {
  return (
    <div role="status" className={`rounded-card font-body px-4 py-3 text-sm ${BANNER_TONES[tone]}`}>
      {children}
    </div>
  );
}

/* ------------------------------- StateScreen ------------------------------ */

export function StateScreen({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <main className="max-w-content mx-auto flex min-h-[60vh] flex-col items-start justify-center px-5">
      <h1 className="font-heading text-ink mb-3 text-3xl">{title}</h1>
      {body ? <p className="font-body text-text mb-6 max-w-xl text-lg">{body}</p> : null}
      {action}
    </main>
  );
}

/* --------------------------------- PriceTag -------------------------------- */

/** BR-PRICE-03 — gross is the headline, net is the small print, always with a unit. */
export function PriceTag({
  grossCents,
  netCents,
  period,
  netLabel,
}: {
  grossCents: number;
  netCents?: number;
  period?: Period;
  netLabel?: string;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="font-heading text-ink text-xl tabular-nums">{formatMoney(grossCents)}</span>
      {period ? <span className="font-body text-muted text-sm">{formatPeriod(period)}</span> : null}
      {netCents !== undefined ? (
        <span className="font-body text-placeholder text-xs tabular-nums">
          {netLabel ?? `${formatMoney(netCents)} netto`}
        </span>
      ) : null}
    </div>
  );
}

/* -------------------------------- FloorPlan ------------------------------- */

export interface FloorPlanProps {
  objects: PlanObject[];
  stateOf: (resourceId: string) => DeskState;
  onSelect?: (resourceId: string) => void;
  t: (key: string) => string;
  /** Rendered behind the desks; comes from `/locations/{id}/plan` as a signed URL. */
  backgroundUrl?: string | null;
  backgroundOpacity?: number;
}

/**
 * 03 § 4.12 — the SVG renderer. Every desk is a real `<button>`, so the plan is
 * usable with a keyboard and a screen reader; the list alternative of § 4.12
 * lives in the calling screen.
 */
export function FloorPlan({
  objects,
  stateOf,
  onSelect,
  t,
  backgroundUrl,
  backgroundOpacity = 1,
}: FloorPlanProps) {
  const box = viewBoxFor(objects);

  return (
    <svg
      role="group"
      aria-label={t('plan.legend.free')}
      viewBox={`${box.minX} ${box.minY} ${box.width} ${box.height}`}
      className="h-auto w-full"
    >
      {backgroundUrl ? (
        <image
          href={backgroundUrl}
          x={box.minX}
          y={box.minY}
          width={box.width}
          height={box.height}
          opacity={backgroundOpacity}
          preserveAspectRatio="xMidYMid slice"
        />
      ) : null}

      {objects.map((object) => {
        const state = stateOf(object.resourceId);
        const style = deskStyle(state);
        const rect = hitRect(object);
        const interactive = onSelect !== undefined && (state === 'free' || state === 'mine');

        return (
          <g
            key={object.resourceId}
            transform={
              object.rotationDeg
                ? `rotate(${object.rotationDeg} ${rect.x + rect.width / 2} ${rect.y + rect.height / 2})`
                : undefined
            }
          >
            <rect
              x={rect.x}
              y={rect.y}
              width={rect.width}
              height={rect.height}
              rx={8}
              fill={style.fill}
              stroke={style.stroke}
              strokeWidth={style.strokeWidth}
              strokeDasharray={style.dash ?? undefined}
              role={interactive ? 'button' : 'img'}
              tabIndex={interactive ? 0 : -1}
              aria-label={deskAccessibilityLabel(object, state, t)}
              aria-disabled={interactive ? undefined : true}
              onClick={interactive ? () => onSelect?.(object.resourceId) : undefined}
              onKeyDown={
                interactive
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onSelect?.(object.resourceId);
                      }
                    }
                  : undefined
              }
              style={{ cursor: interactive ? 'pointer' : 'default' }}
            />
            <text
              x={rect.x + rect.width / 2}
              y={rect.y + rect.height / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fill={style.label}
              fontSize={Math.min(rect.height * 0.4, 28)}
              fontFamily="Figtree, sans-serif"
              fontWeight={600}
              pointerEvents="none"
            >
              {object.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** The list alternative the a11y minimums require next to every plan (§ 4.12). */
export function FloorPlanList({
  objects,
  stateOf,
  onSelect,
  t,
}: Omit<FloorPlanProps, 'backgroundUrl' | 'backgroundOpacity'>) {
  return (
    <ul className="flex flex-col gap-2">
      {objects.map((object) => {
        const state = stateOf(object.resourceId);
        const selectable = state === 'free' || state === 'mine';
        return (
          <li key={object.resourceId}>
            <button
              type="button"
              disabled={!selectable}
              onClick={() => onSelect?.(object.resourceId)}
              className="border-line rounded-card disabled:text-placeholder flex w-full items-center justify-between border px-4 py-3 text-left disabled:cursor-not-allowed"
            >
              <span className="font-body text-ink">{object.label}</span>
              <span className="font-body text-muted text-sm">{deskAccessibilityLabel(object, state, t)}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/* -------------------------------- OtpInput -------------------------------- */

export interface OtpInputProps {
  /** How many boxes. Six for an e-mail or app code, eight for a recovery code. */
  length?: number;
  value: string;
  onChange(value: string): void;
  /** Fired once the last box is filled (06 § 5.2 auto-submit). */
  onComplete?(value: string): void;
  /** An i18n-resolved message; the boxes are only in the error style when set. */
  error?: string | null;
  label: string;
  disabled?: boolean;
}

/**
 * The six-box code input of 06 § 5.2 and 09 § 4.1.
 *
 * One hidden-in-plain-sight input rather than six separate ones: the browser's
 * own `one-time-code` autofill and a pasted code both land in a single field,
 * and six fields fight over which of them receives them. The boxes below are
 * drawn from its value and the caret is faked, which also keeps the whole
 * thing a single tab stop.
 */
export function OtpInput({ length = 6, value, onChange, onComplete, error, label, disabled }: OtpInputProps) {
  const id = 'otp-input';
  const digits = Array.from({ length }, (_, index) => value[index] ?? '');
  const field = useRef<HTMLInputElement>(null);

  // 06 § 5.2 — the screen exists to take a code, and after a wrong one the
  // input is cleared *and* focused. A ref does both; `autoFocus` only the first.
  useEffect(() => {
    field.current?.focus();
  }, [error]);

  function handle(next: string): void {
    const cleaned = next.replace(/[^0-9A-Za-z]/g, '').slice(0, length);
    onChange(cleaned);
    if (cleaned.length === length) onComplete?.(cleaned);
  }

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <div className={`relative ${error ? 'motion-safe:animate-shake' : ''}`}>
        <input
          id={id}
          ref={field}
          value={value}
          onChange={(event) => handle(event.target.value)}
          disabled={disabled}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={length}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          // Transparent and stretched across the boxes: the real caret and the
          // native selection stay out of sight while every input event still
          // reaches a genuine text field.
          className="absolute inset-0 z-10 h-full w-full cursor-pointer text-transparent caret-transparent opacity-0"
        />
        <div aria-hidden className="flex gap-2">
          {digits.map((digit, index) => (
            <span
              key={index}
              className={[
                'rounded-input h-field font-heading text-ink flex flex-1 items-center justify-center border text-2xl',
                error ? 'border-danger-text' : digit ? 'border-line-strong' : 'border-line',
                !error && index === value.length ? 'ring-teal-dark ring-2' : '',
              ].join(' ')}
            >
              {digit}
            </span>
          ))}
        </div>
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="font-body text-danger-text text-sm">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export { Wordmark, ClaimWoo, AppleGlyph, GoogleGlyph } from './Wordmark.js';
export { LoadingMark, LoadingScreen } from './LoadingMark.js';
